// Modal UI for managing plan_zones on a specific plan (drawing).
// Rectangles and closed freehand polygons; coordinates normalized 0..100%.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { planZonesApi } from './zonesApi';
import { GARDEN_SUN_EXPOSURE_OPTIONS } from '../garden/constants';

const ZONE_COLORS = [
  { name: 'Green',  stroke: '#16a34a', fill: 'rgba(22,163,74,0.18)' },
  { name: 'Blue',   stroke: '#2563eb', fill: 'rgba(37,99,235,0.18)' },
  { name: 'Purple', stroke: '#7c3aed', fill: 'rgba(124,58,237,0.18)' },
  { name: 'Orange', stroke: '#ea580c', fill: 'rgba(234,88,12,0.18)' },
  { name: 'Pink',   stroke: '#db2777', fill: 'rgba(219,39,119,0.18)' },
];

const FREEHAND_SAMPLE_MIN = 0.35;
const MIN_RECT_SIZE = 1;
const MIN_POLY_AREA = 0.15;
const RDP_EPSILON = 0.4;

function colorForIndex(i) { return ZONE_COLORS[i % ZONE_COLORS.length]; }

function hexToFillRgba(hex, alpha = 0.18) {
  const m = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex || '');
  if (!m) return `rgba(22,163,74,${alpha})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
}

function polygonArea(points) {
  if (!points || points.length < 3) return 0;
  let a = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    a += points[j].x * points[i].y - points[i].x * points[j].y;
  }
  return Math.abs(a / 2);
}

function dist2(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function rdpSimplify(points, epsilon) {
  if (points.length < 3) return points;
  const sqEps = epsilon * epsilon;

  function simplifyRange(start, end, acc) {
    let maxD = 0;
    let maxI = start;
    const a = points[start];
    const b = points[end];
    for (let i = start + 1; i < end; i++) {
      const p = points[i];
      const d = perpDist2(a, b, p);
      if (d > maxD) {
        maxD = d;
        maxI = i;
      }
    }
    if (maxD > sqEps) {
      simplifyRange(start, maxI, acc);
      acc.push(points[maxI]);
      simplifyRange(maxI, end, acc);
    }
  }

  function perpDist2(p1, p2, p) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-12) return dist2(p, p1);
    let t = ((p.x - p1.x) * dx + (p.y - p1.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const nx = p1.x + t * dx;
    const ny = p1.y + t * dy;
    return dist2(p, { x: nx, y: ny });
  }

  const acc = [points[0]];
  simplifyRange(0, points.length - 1, acc);
  acc.push(points[points.length - 1]);
  return acc;
}

function oppositeCorner(corner, r) {
  const { x, y, width: w, height: h } = r;
  switch (corner) {
    case 'nw': return { x: x + w, y: y + h };
    case 'ne': return { x, y: y + h };
    case 'se': return { x, y };
    case 'sw': return { x: x + w, y };
    default: return { x, y };
  }
}

function rectFromAnchorAndPoint(corner, anchor, px, py) {
  const x1 = Math.min(anchor.x, px);
  const y1 = Math.min(anchor.y, py);
  const x2 = Math.max(anchor.x, px);
  const y2 = Math.max(anchor.y, py);
  return {
    x: Math.max(0, Math.min(100, x1)),
    y: Math.max(0, Math.min(100, y1)),
    width: Math.max(0, Math.min(100, x2) - Math.max(0, x1)),
    height: Math.max(0, Math.min(100, y2) - Math.max(0, y1)),
  };
}

function clampRect(r) {
  let { x, y, width: w, height: h } = r;
  x = Math.max(0, Math.min(100, x));
  y = Math.max(0, Math.min(100, y));
  w = Math.max(0, Math.min(100 - x, w));
  h = Math.max(0, Math.min(100 - y, h));
  return { x, y, width: w, height: h };
}

function bboxForShape(shapeType, shape) {
  if (shapeType === 'polygon') {
    const pts = Array.isArray(shape?.points) ? shape.points : [];
    if (!pts.length) return { x: 0, y: 0, w: 0, h: 0 };
    let minX = 100;
    let minY = 100;
    let maxX = 0;
    let maxY = 0;
    for (const p of pts) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }
  const s = shape || {};
  return {
    x: Number(s.x) || 0,
    y: Number(s.y) || 0,
    w: Number(s.width) || 0,
    h: Number(s.height) || 0,
  };
}

export default function ZoneManager({
  projectId,
  planId,
  planImageUrl,
  onClose,
  onSaved,
}) {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState('idle');
  const [drawTool, setDrawTool] = useState('rect');
  const [rectDraft, setRectDraft] = useState(null);
  const [freehandDraft, setFreehandDraft] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneSeason, setNewZoneSeason] = useState('spring');
  const [newZoneSun, setNewZoneSun] = useState('');
  const [newZoneNotes, setNewZoneNotes] = useState('');

  const [shapeOverride, setShapeOverride] = useState(null);
  const shapeOverrideRef = useRef(null);
  const shapeEditRef = useRef(null);
  const globalShapeCleanupRef = useRef(null);

  const imgWrapRef = useRef(null);
  const freehandCaptureId = useRef(null);
  const pointerCaptureElRef = useRef(null);
  const rectDraftRef = useRef(null);
  const freehandDraftRef = useRef(null);

  useEffect(() => {
    shapeOverrideRef.current = shapeOverride;
  }, [shapeOverride]);

  useEffect(() => {
    rectDraftRef.current = rectDraft;
  }, [rectDraft]);

  useEffect(() => {
    freehandDraftRef.current = freehandDraft;
  }, [freehandDraft]);

  useEffect(() => () => {
    if (globalShapeCleanupRef.current) globalShapeCleanupRef.current();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await planZonesApi.list(projectId, { planId });
        if (cancelled) return;
        setZones(r.zones || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, planId]);

  const toPercent = useCallback((evt) => {
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    let cx;
    let cy;
    if (evt.pointerType === 'touch' && evt.pressure === 0 && 'clientX' in evt) {
      cx = evt.clientX;
      cy = evt.clientY;
    } else {
      cx = evt.clientX;
      cy = evt.clientY;
    }
    const x = Math.min(100, Math.max(0, ((cx - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((cy - rect.top) / rect.height) * 100));
    return { x, y };
  }, []);

  const getZoneDisplayShape = useCallback((z) => {
    if (shapeOverride && shapeOverride.zoneId === z.id) {
      return { shape_type: shapeOverride.shape_type, shape: shapeOverride.shape };
    }
    return { shape_type: z.shape_type, shape: z.shape_json || {} };
  }, [shapeOverride]);

  const zoneRenderList = useMemo(() => {
    return zones.map((z, i) => {
      const color = z.color || colorForIndex(i).stroke;
      const fill = hexToFillRgba(color, 0.18);
      const { shape_type: st, shape } = getZoneDisplayShape(z);
      const bbox = bboxForShape(st, shape);
      return {
        id: z.id,
        name: z.name,
        color,
        fill,
        shape_type: st,
        shape,
        bbox,
        zIndex: i,
      };
    });
  }, [zones, getZoneDisplayShape]);

  async function persistZoneShape(zoneId, shapeType, shape) {
    const r = await planZonesApi.update(zoneId, { shape_type: shapeType, shape });
    setZones((list) => list.map((z) => (z.id === zoneId ? r.zone : z)));
  }

  const commitRectDraw = useCallback(async (draft) => {
    if (!draft) return;
    const x = Math.min(draft.startX, draft.endX);
    const y = Math.min(draft.startY, draft.endY);
    const width = Math.abs(draft.endX - draft.startX);
    const height = Math.abs(draft.endY - draft.startY);
    if (width < MIN_RECT_SIZE || height < MIN_RECT_SIZE) {
      rectDraftRef.current = null;
      setRectDraft(null);
      return;
    }
    const name = newZoneName.trim() || `Zone ${zones.length + 1}`;
    try {
      setSaving(true);
      const r = await planZonesApi.create({
        project_id: projectId,
        plan_id: planId,
        name,
        season_default: newZoneSeason,
        shape_type: 'rect',
        shape: { x, y, width, height },
        color: colorForIndex(zones.length).stroke,
        sun_exposure: newZoneSun.trim() || undefined,
        site_notes: newZoneNotes.trim() || undefined,
      });
      setZones((z) => [...z, r.zone]);
      setNewZoneName('');
      setNewZoneSun('');
      setNewZoneNotes('');
      setMode('idle');
      setRectDraft(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [newZoneName, newZoneSeason, newZoneSun, newZoneNotes, planId, projectId, zones.length]);

  const commitFreehandDraw = useCallback(async (rawPoints) => {
    if (!rawPoints || rawPoints.length < 3) {
      setFreehandDraft(null);
      return;
    }
    let pts = rdpSimplify(rawPoints, RDP_EPSILON);
    if (pts.length < 3) pts = rawPoints;
    if (dist2(pts[0], pts[pts.length - 1]) < FREEHAND_SAMPLE_MIN * FREEHAND_SAMPLE_MIN) {
      pts = pts.slice(0, -1);
    }
    if (pts.length < 3) {
      setError('Draw a larger closed shape (at least three corners).');
      setFreehandDraft(null);
      return;
    }
    const area = polygonArea(pts);
    if (area < MIN_POLY_AREA) {
      setError('Zone too small — draw a slightly larger area.');
      setFreehandDraft(null);
      return;
    }
    setError(null);
    const name = newZoneName.trim() || `Zone ${zones.length + 1}`;
    try {
      setSaving(true);
      const r = await planZonesApi.create({
        project_id: projectId,
        plan_id: planId,
        name,
        season_default: newZoneSeason,
        shape_type: 'polygon',
        shape: { points: pts.map((p) => ({ x: p.x, y: p.y })) },
        color: colorForIndex(zones.length).stroke,
        sun_exposure: newZoneSun.trim() || undefined,
        site_notes: newZoneNotes.trim() || undefined,
      });
      setZones((z) => [...z, r.zone]);
      setNewZoneName('');
      setNewZoneSun('');
      setNewZoneNotes('');
      setMode('idle');
      setFreehandDraft(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [newZoneName, newZoneSeason, newZoneSun, newZoneNotes, planId, projectId, zones.length]);

  function startRectDraw(e) {
    if (mode !== 'drawing' || drawTool !== 'rect') return;
    const p = toPercent(e);
    if (!p) return;
    const d = { startX: p.x, startY: p.y, endX: p.x, endY: p.y };
    rectDraftRef.current = d;
    setRectDraft(d);
  }

  function moveRectDraw(e) {
    if (mode !== 'drawing' || drawTool !== 'rect') return;
    const d0 = rectDraftRef.current;
    if (!d0) return;
    const p = toPercent(e);
    if (!p) return;
    const d = { ...d0, endX: p.x, endY: p.y };
    rectDraftRef.current = d;
    setRectDraft(d);
  }

  function endRectDraw() {
    if (mode !== 'drawing' || drawTool !== 'rect') return;
    const d = rectDraftRef.current;
    rectDraftRef.current = null;
    setRectDraft(null);
    if (d) commitRectDraw(d);
  }

  function startFreehandDraw(e) {
    if (mode !== 'drawing' || drawTool !== 'freehand') return;
    const p = toPercent(e);
    if (!p) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
      pointerCaptureElRef.current = e.currentTarget;
      freehandCaptureId.current = e.pointerId;
    } catch (_) { /* ignore */ }
    const fd = { points: [{ x: p.x, y: p.y }] };
    freehandDraftRef.current = fd;
    setFreehandDraft(fd);
  }

  function moveFreehandDraw(e) {
    if (mode !== 'drawing' || drawTool !== 'freehand') return;
    const fd = freehandDraftRef.current;
    if (!fd || !fd.points.length) return;
    const p = toPercent(e);
    if (!p) return;
    const last = fd.points[fd.points.length - 1];
    if (dist2(last, p) < FREEHAND_SAMPLE_MIN * FREEHAND_SAMPLE_MIN) return;
    const next = { points: [...fd.points, { x: p.x, y: p.y }] };
    freehandDraftRef.current = next;
    setFreehandDraft(next);
  }

  function endFreehandDraw(e) {
    if (drawTool !== 'freehand') return;
    if (freehandCaptureId.current != null && e && 'pointerId' in e) {
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
      } catch (_) { /* ignore */ }
      freehandCaptureId.current = null;
      pointerCaptureElRef.current = null;
    }
    if (mode !== 'drawing') return;
    const pts = freehandDraftRef.current?.points;
    freehandDraftRef.current = null;
    setFreehandDraft(null);
    if (pts && pts.length) commitFreehandDraw(pts);
  }

  function cancelFreehandDraw() {
    const el = pointerCaptureElRef.current;
    const pid = freehandCaptureId.current;
    if (el != null && pid != null) {
      try {
        if (el.hasPointerCapture(pid)) el.releasePointerCapture(pid);
      } catch (_) { /* ignore */ }
    }
    pointerCaptureElRef.current = null;
    freehandCaptureId.current = null;
    freehandDraftRef.current = null;
    setFreehandDraft(null);
  }

  function onDrawingLayerLeave() {
    if (mode !== 'drawing') return;
    if (drawTool === 'rect' && rectDraftRef.current) {
      const d = rectDraftRef.current;
      rectDraftRef.current = null;
      setRectDraft(null);
      commitRectDraw(d);
    }
    if (drawTool === 'freehand') {
      cancelFreehandDraw();
      setError(null);
    }
  }

  function onBackdropPointerDown(e) {
    if (mode === 'drawing') return;
    if (e.target === e.currentTarget) {
      setSelectedId(null);
      setShapeOverride(null);
      shapeOverrideRef.current = null;
    }
  }

  function clearGlobalShapeListeners() {
    const fn = globalShapeCleanupRef.current;
    if (fn) {
      fn();
      globalShapeCleanupRef.current = null;
    }
  }

  function finishShapeEditInner() {
    const ed = shapeEditRef.current;
    shapeEditRef.current = null;
    clearGlobalShapeListeners();
    const ov = shapeOverrideRef.current;
    if (!ed || !ov || ov.zoneId !== ed.zoneId) {
      setShapeOverride(null);
      shapeOverrideRef.current = null;
      return;
    }
    const { zoneId, shape_type: st, shape } = ov;
    (async () => {
      try {
        setSaving(true);
        await persistZoneShape(zoneId, st, shape);
        setShapeOverride(null);
        shapeOverrideRef.current = null;
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
    })();
  }

  function attachGlobalShapeListeners() {
    clearGlobalShapeListeners();
    const onMove = (e) => {
      const ed = shapeEditRef.current;
      if (!ed) return;
      const p = toPercent(e);
      if (!p) return;

      let next = null;
      if (ed.kind === 'rect_move') {
        const dx = p.x - ed.start.x;
        const dy = p.y - ed.start.y;
        let nx = ed.orig.x + dx;
        let ny = ed.orig.y + dy;
        nx = Math.max(0, Math.min(100 - ed.orig.width, nx));
        ny = Math.max(0, Math.min(100 - ed.orig.height, ny));
        next = {
          zoneId: ed.zoneId,
          shape_type: 'rect',
          shape: { x: nx, y: ny, width: ed.orig.width, height: ed.orig.height },
        };
      } else if (ed.kind === 'rect_resize') {
        const raw = rectFromAnchorAndPoint(ed.corner, ed.anchor, p.x, p.y);
        const clamped = clampRect(raw);
        if (clamped.width < MIN_RECT_SIZE || clamped.height < MIN_RECT_SIZE) return;
        next = { zoneId: ed.zoneId, shape_type: 'rect', shape: clamped };
      } else if (ed.kind === 'poly_move') {
        const dx = p.x - ed.start.x;
        const dy = p.y - ed.start.y;
        const nextPts = ed.origPoints.map((q) => ({
          x: Math.min(100, Math.max(0, q.x + dx)),
          y: Math.min(100, Math.max(0, q.y + dy)),
        }));
        next = { zoneId: ed.zoneId, shape_type: 'polygon', shape: { points: nextPts } };
      } else if (ed.kind === 'poly_vertex') {
        const nextPts = ed.origPoints.map((q, j) =>
          (j === ed.vertexIndex
            ? { x: Math.min(100, Math.max(0, p.x)), y: Math.min(100, Math.max(0, p.y)) }
            : { ...q })
        );
        next = { zoneId: ed.zoneId, shape_type: 'polygon', shape: { points: nextPts } };
      }
      if (next) {
        shapeOverrideRef.current = next;
        setShapeOverride(next);
      }
    };
    const onUp = () => {
      finishShapeEditInner();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    globalShapeCleanupRef.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }

  function onShapePointerDown(zone, e) {
    if (mode === 'drawing') return;
    e.stopPropagation();
    setSelectedId(zone.id);
    const p = toPercent(e);
    if (!p) return;
    const { shape_type: st, shape: sh } = getZoneDisplayShape(zone);
    if (st === 'rect') {
      const r = {
        x: Number(sh.x) || 0,
        y: Number(sh.y) || 0,
        width: Number(sh.width) || 0,
        height: Number(sh.height) || 0,
      };
      shapeEditRef.current = {
        kind: 'rect_move',
        zoneId: zone.id,
        start: p,
        orig: { ...r },
      };
    } else if (st === 'polygon') {
      const pts = (Array.isArray(sh.points) ? sh.points : []).map((q) => ({ x: Number(q.x) || 0, y: Number(q.y) || 0 }));
      shapeEditRef.current = {
        kind: 'poly_move',
        zoneId: zone.id,
        start: p,
        origPoints: pts.map((q) => ({ ...q })),
      };
    }
    attachGlobalShapeListeners();
  }

  function onRectCornerPointerDown(zone, corner, e) {
    if (mode === 'drawing') return;
    e.stopPropagation();
    setSelectedId(zone.id);
    const { shape_type: st, shape: sh } = getZoneDisplayShape(zone);
    if (st !== 'rect') return;
    const r = {
      x: Number(sh.x) || 0,
      y: Number(sh.y) || 0,
      width: Number(sh.width) || 0,
      height: Number(sh.height) || 0,
    };
    shapeEditRef.current = {
      kind: 'rect_resize',
      zoneId: zone.id,
      corner,
      anchor: oppositeCorner(corner, r),
      orig: { ...r },
    };
    attachGlobalShapeListeners();
  }

  function onPolyVertexPointerDown(zone, vi, e) {
    if (mode === 'drawing') return;
    e.stopPropagation();
    setSelectedId(zone.id);
    const { shape_type: st, shape: sh } = getZoneDisplayShape(zone);
    if (st !== 'polygon') return;
    const pts = (Array.isArray(sh.points) ? sh.points : []).map((q) => ({ x: Number(q.x) || 0, y: Number(q.y) || 0 }));
    shapeEditRef.current = {
      kind: 'poly_vertex',
      zoneId: zone.id,
      vertexIndex: vi,
      origPoints: pts.map((q) => ({ ...q })),
    };
    attachGlobalShapeListeners();
  }

  async function handleDeleteZone(id) {
    if (!window.confirm('Delete this zone?')) return;
    try {
      await planZonesApi.remove(id);
      setZones((list) => list.filter((z) => z.id !== id));
      if (selectedId === id) setSelectedId(null);
      if (shapeOverride?.zoneId === id) {
        setShapeOverride(null);
        shapeOverrideRef.current = null;
      }
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleRenameZone(id, name) {
    const next = (name || '').trim();
    if (!next) return;
    try {
      const r = await planZonesApi.update(id, { name: next });
      setZones((list) => list.map((z) => (z.id === id ? r.zone : z)));
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleSeasonChange(id, season) {
    try {
      const r = await planZonesApi.update(id, { season_default: season });
      setZones((list) => list.map((z) => (z.id === id ? r.zone : z)));
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleSunChange(id, sun) {
    try {
      const r = await planZonesApi.update(id, { sun_exposure: sun || null });
      setZones((list) => list.map((z) => (z.id === id ? r.zone : z)));
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleSiteNotesBlur(zone, nextRaw) {
    const next = (nextRaw || '').trim();
    const prev = (zone.site_notes || '').trim();
    if (next === prev) return;
    try {
      const r = await planZonesApi.update(zone.id, { site_notes: next || null });
      setZones((list) => list.map((z) => (z.id === zone.id ? r.zone : z)));
    } catch (e) {
      setError(e.message);
    }
  }

  const pointsAttr = (pts) => (pts || []).map((p) => `${p.x},${p.y}`).join(' ');

  const selectedZone = zones.find((z) => z.id === selectedId);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Zones on this plan</h2>
            <p className="text-sm text-gray-600">Draw named regions (rectangle or freehand). Set sun and site notes — they carry through when you send this to the homeowner.</p>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 text-xl"
            onClick={() => {
              if (onSaved) onSaved(zones);
              if (onClose) onClose();
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-6 py-2 border-b border-red-100">
            {error}
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 bg-gray-100 overflow-auto p-4">
            <div
              ref={imgWrapRef}
              className="relative inline-block max-w-full select-none"
              style={{
                cursor:
                  mode === 'drawing'
                    ? drawTool === 'freehand'
                      ? 'crosshair'
                      : 'crosshair'
                    : 'default',
                touchAction: mode === 'drawing' ? 'none' : 'auto',
              }}
            >
              {planImageUrl ? (
                <img
                  src={planImageUrl}
                  alt="Plan"
                  draggable={false}
                  className="max-w-full max-h-[70vh] select-none pointer-events-none block"
                />
              ) : (
                <div className="w-[600px] h-[400px] bg-white flex items-center justify-center text-gray-500">
                  No plan image to annotate.
                </div>
              )}

              <div
                className="absolute inset-0"
                style={{ zIndex: 1 }}
              >
                <div
                  role="presentation"
                  className="absolute inset-0 bg-transparent"
                  style={{ zIndex: 0 }}
                  onPointerDown={onBackdropPointerDown}
                />
                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ zIndex: 1, pointerEvents: mode === 'drawing' ? 'none' : 'auto' }}
                >
                  {zoneRenderList.map((r) => {
                    const isSel = selectedId === r.id;
                    if (r.shape_type === 'polygon') {
                      const pts = r.shape?.points || [];
                      if (pts.length < 3) return null;
                      return (
                        <polygon
                          key={r.id}
                          data-zone-id={r.id}
                          points={pointsAttr(pts)}
                          fill={r.fill}
                          stroke={r.color}
                          strokeWidth={isSel ? 0.35 : 0.22}
                          vectorEffect="non-scaling-stroke"
                          style={{ cursor: mode === 'idle' ? 'move' : 'default' }}
                          onPointerDown={(e) => onShapePointerDown(zones.find((z) => z.id === r.id), e)}
                        />
                      );
                    }
                    const s = r.shape || {};
                    const x = Number(s.x) || 0;
                    const y = Number(s.y) || 0;
                    const w = Number(s.width) || 0;
                    const h = Number(s.height) || 0;
                    return (
                      <rect
                        key={r.id}
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        rx={0.4}
                        fill={r.fill}
                        stroke={r.color}
                        strokeWidth={isSel ? 0.35 : 0.22}
                        vectorEffect="non-scaling-stroke"
                        style={{ cursor: mode === 'idle' ? 'move' : 'default' }}
                        onPointerDown={(e) => onShapePointerDown(zones.find((z) => z.id === r.id), e)}
                      />
                    );
                  })}
                </svg>

                {zoneRenderList.map((r) => {
                  const { bbox } = r;
                  const top = Math.max(0, bbox.y - 3);
                  return (
                    <div
                      key={`label-${r.id}`}
                      className="absolute pointer-events-none text-xs font-semibold px-1.5 py-0.5 rounded text-white whitespace-nowrap"
                      style={{
                        left: `${bbox.x}%`,
                        top: `${top}%`,
                        background: r.color,
                        zIndex: 2,
                        transform: 'translateY(-100%)',
                      }}
                    >
                      {r.name}
                    </div>
                  );
                })}

                {selectedZone && mode === 'idle' && (() => {
                  const disp = getZoneDisplayShape(selectedZone);
                  const st = disp.shape_type;
                  const sh = disp.shape || {};
                  if (st === 'rect') {
                    const x = Number(sh.x) || 0;
                    const y = Number(sh.y) || 0;
                    const w = Number(sh.width) || 0;
                    const h = Number(sh.height) || 0;
                    const corners = [
                      { k: 'nw', cx: x, cy: y },
                      { k: 'ne', cx: x + w, cy: y },
                      { k: 'se', cx: x + w, cy: y + h },
                      { k: 'sw', cx: x, cy: y + h },
                    ];
                    return (
                      <svg
                        key="handles-rect"
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        style={{ zIndex: 3 }}
                      >
                        {corners.map((c) => (
                          <circle
                            key={c.k}
                            cx={c.cx}
                            cy={c.cy}
                            r={1.1}
                            fill="#fff"
                            stroke="#111"
                            strokeWidth={0.15}
                            className="pointer-events-auto cursor-nwse-resize"
                            style={{ cursor: 'nwse-resize' }}
                            onPointerDown={(e) => onRectCornerPointerDown(selectedZone, c.k, e)}
                          />
                        ))}
                      </svg>
                    );
                  }
                  if (st === 'polygon') {
                    const pts = sh.points || [];
                    return (
                      <svg
                        key="handles-poly"
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        style={{ zIndex: 3 }}
                      >
                        {pts.map((p, vi) => (
                          <circle
                            key={vi}
                            cx={p.x}
                            cy={p.y}
                            r={1.1}
                            fill="#fff"
                            stroke="#111"
                            strokeWidth={0.15}
                            className="pointer-events-auto cursor-grab"
                            onPointerDown={(e) => onPolyVertexPointerDown(selectedZone, vi, e)}
                          />
                        ))}
                      </svg>
                    );
                  }
                  return null;
                })()}

                {mode === 'drawing' && (
                  <div
                    role="presentation"
                    className="absolute inset-0 z-[20]"
                    style={{ touchAction: 'none' }}
                    onPointerDown={drawTool === 'rect' ? startRectDraw : startFreehandDraw}
                    onPointerMove={drawTool === 'rect' ? moveRectDraw : moveFreehandDraw}
                    onPointerUp={drawTool === 'rect' ? endRectDraw : endFreehandDraw}
                    onPointerCancel={drawTool === 'freehand' ? () => { cancelFreehandDraw(); } : undefined}
                    onPointerLeave={onDrawingLayerLeave}
                  />
                )}

                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ zIndex: 15 }}
                >
                  {rectDraft && drawTool === 'rect' && (() => {
                    const x = Math.min(rectDraft.startX, rectDraft.endX);
                    const y = Math.min(rectDraft.startY, rectDraft.endY);
                    const w = Math.abs(rectDraft.endX - rectDraft.startX);
                    const h = Math.abs(rectDraft.endY - rectDraft.startY);
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill="rgba(22,163,74,0.12)"
                        stroke="#16a34a"
                        strokeWidth={0.2}
                        strokeDasharray="1 0.8"
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })()}
                  {freehandDraft && freehandDraft.points.length > 0 && drawTool === 'freehand' && (
                    <polyline
                      points={pointsAttr(freehandDraft.points)}
                      fill="none"
                      stroke="#16a34a"
                      strokeWidth={0.25}
                      strokeDasharray="0.6 0.5"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                </svg>
              </div>
            </div>
          </div>

          <div className="w-80 border-l flex flex-col overflow-hidden">
            <div className="p-4 border-b space-y-3">
              <div className="text-sm font-semibold text-gray-900">New zone</div>
              <input
                type="text"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                placeholder="Name (e.g. Front Border)"
                className="w-full border rounded px-2 py-1 text-sm"
              />
              <select
                value={newZoneSeason}
                onChange={(e) => setNewZoneSeason(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="spring">Spring photo</option>
                <option value="summer">Summer photo</option>
                <option value="fall">Fall photo</option>
                <option value="winter">Winter photo</option>
              </select>
              <label className="block text-xs text-gray-600">Sun / exposure (optional)</label>
              <select
                value={newZoneSun}
                onChange={(e) => setNewZoneSun(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="">Not set</option>
                {GARDEN_SUN_EXPOSURE_OPTIONS.map((s) => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
              <label className="block text-xs text-gray-600">Site notes (optional)</label>
              <textarea
                value={newZoneNotes}
                onChange={(e) => setNewZoneNotes(e.target.value)}
                rows={2}
                placeholder="e.g. Afternoon shade from the house; stays wet in spring"
                className="w-full border rounded px-2 py-1 text-sm resize-y min-h-[2.5rem]"
              />
              <div className="flex rounded border overflow-hidden">
                <button
                  type="button"
                  className={`flex-1 px-2 py-1.5 text-xs font-medium ${drawTool === 'rect' ? 'bg-gray-800 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => setDrawTool('rect')}
                  disabled={mode === 'drawing'}
                >
                  Rectangle
                </button>
                <button
                  type="button"
                  className={`flex-1 px-2 py-1.5 text-xs font-medium ${drawTool === 'freehand' ? 'bg-gray-800 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => setDrawTool('freehand')}
                  disabled={mode === 'drawing'}
                >
                  Freehand
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (mode === 'drawing') {
                    if (drawTool === 'freehand') cancelFreehandDraw();
                    rectDraftRef.current = null;
                    setRectDraft(null);
                    setMode('idle');
                  } else {
                    setMode('drawing');
                  }
                }}
                className={`w-full px-3 py-2 rounded text-sm font-medium ${
                  mode === 'drawing'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                disabled={saving || !planImageUrl}
              >
                {mode === 'drawing'
                  ? (drawTool === 'rect' ? 'Drawing… drag a rectangle on the plan' : 'Drawing… trace the zone on the plan')
                  : 'Draw new zone'}
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-2">
              <div className="text-sm font-semibold text-gray-900 mb-2">
                {loading ? 'Loading…' : `Zones (${zones.length})`}
              </div>
              {zones.length === 0 && !loading && (
                <p className="text-sm text-gray-500">
                  No zones yet. Use rectangle or freehand to outline beds, borders, or patios on the plan. Select a zone to resize or move it.
                </p>
              )}
              {zones.map((z, i) => {
                const color = z.color || colorForIndex(i).stroke;
                const shapeLabel = z.shape_type === 'polygon' ? 'Freehand' : 'Rectangle';
                return (
                  <div
                    key={z.id}
                    className={`border rounded p-2 ${selectedId === z.id ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => setSelectedId(z.id)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded shrink-0" style={{ background: color }} />
                      <input
                        className="flex-1 text-sm font-medium bg-transparent border-b border-transparent focus:border-gray-300 outline-none min-w-0"
                        defaultValue={z.name}
                        onBlur={(e) => {
                          if (e.target.value !== z.name) handleRenameZone(z.id, e.target.value);
                        }}
                      />
                      <span className="text-[10px] text-gray-400 shrink-0">{shapeLabel}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeleteZone(z.id); }}
                        className="text-xs text-red-600 hover:text-red-800 shrink-0"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <span>Default photo:</span>
                      <select
                        className="border rounded px-1 py-0.5"
                        value={z.season_default || 'spring'}
                        onChange={(e) => handleSeasonChange(z.id, e.target.value)}
                      >
                        <option value="spring">Spring</option>
                        <option value="summer">Summer</option>
                        <option value="fall">Fall</option>
                        <option value="winter">Winter</option>
                      </select>
                    </div>
                    <div className="mt-2 space-y-1">
                      <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide">Sun</label>
                      <select
                        className="w-full border rounded px-1 py-0.5 text-xs"
                        value={z.sun_exposure || ''}
                        onChange={(e) => handleSunChange(z.id, e.target.value)}
                      >
                        <option value="">Not set</option>
                        {GARDEN_SUN_EXPOSURE_OPTIONS.map((s) => (
                          <option key={s} value={s} className="capitalize">{s}</option>
                        ))}
                      </select>
                      <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mt-1">Site notes</label>
                      <textarea
                        defaultValue={z.site_notes || ''}
                        key={`${z.id}-notes`}
                        rows={2}
                        onBlur={(e) => handleSiteNotesBlur(z, e.target.value)}
                        placeholder="Optional — microclimate, drainage, etc."
                        className="w-full border rounded px-1 py-0.5 text-xs resize-y min-h-[2.25rem]"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t">
              <button
                type="button"
                className="w-full px-3 py-2 bg-gray-800 text-white rounded text-sm font-medium hover:bg-gray-900"
                onClick={() => {
                  if (onSaved) onSaved(zones);
                  if (onClose) onClose();
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
